import * as buttons from 'smg/buttons';
import { GetChildren, GetLineItems, GetSizes, GetTypes } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import * as quoterequests from 'smg/customer-api/quoterequests';
import { Session } from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { BlockPastDates, LocalDate, ReportValidity } from 'smg/form';
import { CleanNumber } from 'smg/number';
import { Phone } from 'smg/phone';
import * as template from 'smg/template';
import { Uploader } from 'smg/uploader/v2';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly quoteReceived: string;
    dataLayer: Record<string, unknown>[];
}

declare const window: myWindow;

interface extra {
    name: string;
    stepContainer: HTMLElement;
    labels: HTMLLabelElement[];
    currentSelection?: HTMLInputElement;
}

void main().finally(() => {
    document.querySelector('#wizard-skeleton')?.remove();
});

async function main() {
    const $form = document.querySelector('#form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;
    const $skeleton = $form.querySelector('#wizard-skeleton') as HTMLElement;
    const $infoContainer = $form.querySelector('#info') as HTMLElement;
    const $lanyardInfo = $form.querySelector('#lanyard-info') as HTMLElement;
    const $quantity = $infoContainer.querySelector('#quantity') as HTMLInputElement;
    const $projectName = $infoContainer.querySelector('#project-name') as HTMLInputElement;
    const $budget = $infoContainer.querySelector('#budget') as HTMLInputElement;
    const $deadlineRadios = Array.from($infoContainer.querySelectorAll('input[name="deadline-radio"]')) as HTMLInputElement[];
    const $dateNeededContainer = $infoContainer.querySelector('#date-needed-container') as HTMLElement;
    const $dateNeeded = $dateNeededContainer.querySelector('#date-needed') as HTMLInputElement;
    const revalidateDateNeeded = BlockPastDates($dateNeeded, { earliest: 'tomorrow' });
    const $uploads = $infoContainer.querySelector('#uploads') as HTMLLabelElement;
    const $thumbContainer = $infoContainer.querySelector('#uploads-thumbnails') as HTMLElement;
    const $projectNotes = $infoContainer.querySelector('#project-notes') as HTMLTextAreaElement;
    const $firstName = $infoContainer.querySelector('#first-name') as HTMLInputElement;
    const $lastName = $infoContainer.querySelector('#last-name') as HTMLInputElement;
    const $email = $infoContainer.querySelector('#email') as HTMLInputElement;
    const $phone = $infoContainer.querySelector('#phone') as HTMLInputElement;
    const $submitBtn = $infoContainer.querySelector('#submit-btn') as HTMLButtonElement;

    const u = new Uploader({
        $input: $uploads.querySelector('input[type="file"]') as HTMLInputElement,
        $dropzones: [$uploads],
        $submitButtons: [$submitBtn],
        $thumbnailsParent: $thumbContainer,
    });

    const p = new Phone($phone);

    let currProductType: string;
    let currSize: string;

    let currStep: HTMLElement;
    let extrasSteps: extra[] = [];

    let lanyardExtras: models.QuoteRequestCreateExtra[] | undefined;

    let headerHeight = (document.querySelector('.top') as HTMLElement).offsetHeight + 10;
    $infoContainer.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
    window.addEventListener('resize', () => {
        if (currStep) {
            headerHeight = (document.querySelector('.top') as HTMLElement).offsetHeight + 10;
            currStep.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
            $infoContainer.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
        }
    });

    // Quantity stepper
    const $qtyMinus = $infoContainer.querySelector('.qty-minus') as HTMLButtonElement;
    const $qtyPlus = $infoContainer.querySelector('.qty-plus') as HTMLButtonElement;
    if ($qtyMinus && $qtyPlus) {
        $qtyMinus.addEventListener('click', () => {
            const val = parseInt($quantity.value, 10) || 100;
            $quantity.value = String(Math.max(1, val - 10));
        });
        $qtyPlus.addEventListener('click', () => {
            const val = parseInt($quantity.value, 10) || 100;
            $quantity.value = String(val + 10);
        });
    }

    // Timeline card selection
    const $timelineCards = Array.from($infoContainer.querySelectorAll('.timeline-card')) as HTMLLabelElement[];
    for (const $radio of $deadlineRadios) {
        $radio.addEventListener('change', () => {
            for (const $card of $timelineCards) {
                $card.classList.toggle('timeline-card--active', ($card.querySelector('input') as HTMLInputElement).checked);
            }
            const $deadline = $infoContainer.querySelector('input[name="deadline-radio"]:checked') as HTMLInputElement;
            if ($deadline.value === 'has-deadline') {
                $dateNeededContainer.classList.add('date-reveal--open');
                $dateNeeded.required = true;
                if (!$dateNeeded.value) {
                    $dateNeeded.value = LocalDate(30);
                }
                revalidateDateNeeded();
            } else {
                $dateNeededContainer.classList.remove('date-reveal--open');
                $dateNeeded.required = false;
                // Revealed by a class, not `disabled` — clear the value so a
                // past date neither blocks submit from behind the collapsed
                // panel nor rides along on the quote request.
                $dateNeeded.value = '';
                revalidateDateNeeded();
            }
        });
    }

    // Upload drag-over styling
    const $uploadZone = $infoContainer.querySelector('.upload-zone') as HTMLElement;
    if ($uploadZone) {
        $uploadZone.addEventListener('dragover', () => $uploadZone.classList.add('drag-over'));
        $uploadZone.addEventListener('dragleave', () => $uploadZone.classList.remove('drag-over'));
        $uploadZone.addEventListener('drop', () => $uploadZone.classList.remove('drag-over'));
    }

    // Click thumbnail to open full image
    $thumbContainer.addEventListener('click', e => {
        const $thumb = (e.target as HTMLElement).closest('.upload-thumbnail');
        const $img = $thumb?.querySelector('img') as HTMLImageElement;
        if ($img?.src && !(e.target as HTMLElement).closest('.upload-thumbnail-close')) {
            window.open($img.src, '_blank');
        }
    });

    // Read URL params from pricing table CTA
    const params = new URLSearchParams(window.location.search);
    const paramType = params.get('type');
    const paramSize = params.get('size');
    const paramQuantity = params.get('quantity');

    if (paramQuantity) {
        $quantity.value = paramQuantity;
    }

    // Skip the product step — auto-select the matching product and go straight to types
    const products = await GetLineItems();
    const preferredLineItem = (window as unknown as Record<string, string>).lineItem;
    const product = preferredLineItem ?
        products.find(p =>
            p.name.localeCompare(preferredLineItem, undefined, { sensitivity: 'base' }) === 0 ||
            p.pluralName.localeCompare(preferredLineItem, undefined, { sensitivity: 'base' }) === 0 ||
            p.networkName().localeCompare(preferredLineItem, undefined, { sensitivity: 'base' }) === 0 ||
            p.networkNamePlural().localeCompare(preferredLineItem, undefined, { sensitivity: 'base' }) === 0,
        ) :
        products.find(p => p.networkLineItem?.enabled);
    if (!product) {
        return;
    }
    const currProduct = product.networkNamePlural();

    // Friendly display names for coin types (API names → display labels)
    const typeDisplayNames: Record<string, string> = {
        'Soft Enamel On Both Sides': 'Color on Both Sides',
        'Soft Enamel On One Side': 'Color on One Side',
    };

    const typeDescriptions: Record<string, string> = {
        'Soft Enamel': 'Soft Enamel custom lapel pins are among our most popular sellers due to their unique 2-D ' +
            'appearance. The raised and recessed areas on the surface of the pin provide dimension without ' +
            'sacrificing the quality craftsmanship that our customers are accustomed to receiving.',
        'Die Struck': 'Die-struck pin gives a classic dimensional look to your design without adding color. Die ' +
            'struck lapel pins are made from either brass or copper that is stamped with the pin design and then ' +
            'cut out. The metal base is then plated with gold, silver, copper or dyed black, and can be antiqued, ' +
            'sandblasted, or simply polished. It is also possible to combine two platings for a traditional style ' +
            'of lapel pin.',
        'Cloisonne': 'Our cloisonné lapel pins continue the fine-art tradition of glass enameling which started ' +
            'in China centuries ago. Cloisonné Lapel Pins were made from hand-pouring powdered glass into the ' +
            'recesses of a pin\u2019s design. The pin is then baked at a very high temperature; the glass enamel ' +
            'is sanded and polished to be flush with the metal lines that separate the colors. This gives the ' +
            'lapel pins a smooth and lustrous quality.',
        'Photo Printed': 'Photo Printed Lapel Pins offer the most flexibility in design and style. Using ' +
            'light-sensitive chemicals and photographic techniques we are able to transfer images and type from ' +
            'digital graphics to printing plates with stunning clarity and accuracy.',
        'Trading Pins': 'Lapel Pins Express customizes a variety of lapel pins for all sports teams, baseball, ' +
            'soccer, basketball, softball, hockey, cheerleading and much, much more. With your ideas our creative ' +
            'artists will help turn your design or team logo into a hot trader. You will in most cases get your ' +
            'artwork back the same day. If any changes are necessary, no problem, it is free of charge.',
        'Silkscreen': 'Silk screen pins offer great detail produced in full color. Using a screen made of finely ' +
            'woven, but porous fabric stretched over a wood or aluminum frame, areas of the screen are blocked ' +
            'off with a non-permeable material. Ink is then placed on top of the screen, and a squeegee is used ' +
            'to push the ink evenly into the screen openings and onto the pin.',
        'Made in USA - Rush Pins': 'Our Rush (USA Printed) pins are premade and ready for printing, perfect for ' +
            'customers with a fast approaching deadline. This style works well with photo backgrounds, ' +
            'complicated corporate logos, and small text.',
    };

    await showTypeStep();

    async function showTypeStep() {
        const title = `What type of ${currProduct} are you looking for?`;
        const $step = createStep(title, ['type-step', `${currProduct}-type-step`]);

        const types = await GetTypes(currProduct);
        $skeleton.remove();
        const $labelEls: HTMLLabelElement[] = [];

        for (const type of types) {
            if (type.networkLineItemType?.enabled) {
                const typeLabel = type.networkNamePlural();
                const desc = typeDescriptions[typeLabel] || typeDescriptions[type.pluralName] || typeDescriptions[type.name] || '';
                const display = typeDisplayNames[typeLabel] || typeDisplayNames[type.pluralName] || typeDisplayNames[type.name] || '';
                $labelEls.push(createInput($step, 'product-type-select', typeLabel, `product-types/${imgNameFormat(currProduct)}/type-${imgNameFormat(typeLabel)}`, desc, display));
            }
        }

        $step.scrollIntoView({ behavior: 'smooth' });

        for (const $label of $labelEls) {
            $label.addEventListener('click', async e => {
                e.preventDefault();
                checkedAndRequired($label.querySelector('input') as HTMLInputElement);
                currProductType = ($label.querySelector('input') as HTMLInputElement).value;
                addRemoveBorders($label, Array.from($step.querySelectorAll('label')));
                removeSteps(['type-step']);
                await showSizeStep(e);
            });
        }

        // Auto-select type from URL param
        if (paramType) {
            const match = $labelEls.find($l => {
                const val = ($l.querySelector('input') as HTMLInputElement).value;
                return val.localeCompare(paramType, undefined, { sensitivity: 'base' }) === 0;
            });
            if (match) {
                match.click();
                return;
            }
        }
    }

    async function showSizeStep(triggerEvent?: Event) {
        const title = `What size ${currProduct} are you looking for?`;
        const $step = createStep(title, ['size-step', `${currProduct}-size-step`]);

        const sizes = await GetSizes(currProduct, currProductType);
        const $labelEls: HTMLLabelElement[] = [];

        for (const size of sizes) {
            if (size.networkLineItemTypeSize?.enabled) {
                const sizeImgName = size.width.toString().replace(/\.?0+$/, '');
                $labelEls.push(createInput($step, 'size-select', size.networkName(), `child-items/${imgNameFormat(currProduct)}/size/${sizeImgName}`));
            }
        }

        if (triggerEvent?.target instanceof HTMLElement && !previouslySelected(triggerEvent.target)) {
            $step.scrollIntoView({ behavior: 'smooth' });
        }

        for (const $label of $labelEls) {
            for (const $name of Array.from($label.querySelectorAll('h4'))) {
                $name.classList.add('fs-3', 'my-10');
            }

            $label.addEventListener('click', async e => {
                e.preventDefault();
                checkedAndRequired($label.querySelector('input') as HTMLInputElement);
                currSize = ($label.querySelector('input') as HTMLInputElement).value;
                addRemoveBorders($label, Array.from($step.querySelectorAll('label')));
                removeSteps(['type-step', 'size-step']);
                await showExtrasSteps(e);
            });
        }

        // Auto-select size from URL param
        if (paramSize) {
            const match = $labelEls.find($l => {
                const val = ($l.querySelector('input') as HTMLInputElement).value;
                return val.localeCompare(paramSize, undefined, { sensitivity: 'base' }) === 0;
            });
            if (match) {
                match.click();
                return;
            }
        }
    }

    async function showExtrasSteps(triggerEvent?: Event) {
        const extras: models.ChildLineItem[] = [];
        const children = await GetChildren(currProduct);
        for (const childLineItem of children) {
            if (childLineItem.networkChildLineItem?.quoteForm && childLineItem.childLineItemOptions.some(o => o.networkChildLineItemOption?.enabled)) {
                extras.push(childLineItem);
            }
        }

        extrasSteps = [];
        const extrasSortOrder = ['Plating', 'Attachment', 'Presentation & Packaging', 'Epoxy Dome'];
        extras.sort((a, b) => extrasSortOrder.indexOf(a.name) - extrasSortOrder.indexOf(b.name));

        for (const childLineItem of extras) {
            const childLabel = childLineItem.networkName();
            const title = `What kind of ${childLabel} would you like?`;
            const $step = createStep(title, [`${childLabel.toLowerCase()}-step`, `${currProduct}-${childLabel.toLowerCase()}-step`], true);

            const $labelEls: HTMLLabelElement[] = [];
            for (const option of childLineItem.childLineItemOptions) {
                if (option.networkChildLineItemOption?.enabled) {
                    const optionLabel = option.networkName();
                    $labelEls.push(createInput(
                        $step,
                        `${childLabel}-select`,
                        optionLabel,
                        `child-items/${imgNameFormat(currProduct)}/${imgNameFormat(childLabel)}/${imgNameFormat(optionLabel)}`,
                    ));
                }
            }
            if (childLineItem.name === 'Border') {
                $labelEls.push(createInput($step, 'border-select', 'None', `child-items/${imgNameFormat(currProduct)}/${imgNameFormat(childLabel)}/${imgNameFormat('none')}`));
            }

            extrasSteps.push({
                name: childLabel,
                stepContainer: $step,
                labels: $labelEls,
            });
        }

        if (extrasSteps.length === 0) {
            showInfoForm();
            return;
        }

        extrasSteps[0].stepContainer.classList.remove('d-none');
        if (triggerEvent?.target instanceof HTMLElement && !previouslySelected(triggerEvent.target)) {
            extrasSteps[0].stepContainer.scrollIntoView({ behavior: 'smooth' });
        }

        for (let i = 0; i < extrasSteps.length; i++) {
            for (const $label of extrasSteps[i].labels) {
                $label.addEventListener('click', e => {
                    e.preventDefault();
                    checkedAndRequired($label.querySelector('input') as HTMLInputElement);
                    extrasSteps[i].currentSelection = $label.querySelector('input') as HTMLInputElement;
                    addRemoveBorders($label, Array.from(extrasSteps[i].stepContainer.querySelectorAll('label')));

                    if (extrasSteps[i + 1]) {
                        extrasSteps[i + 1].stepContainer.classList.remove('d-none');
                        if (e.target instanceof HTMLElement && !previouslySelected(e.target)) {
                            extrasSteps[i + 1].stepContainer.scrollIntoView({ behavior: 'smooth' });
                        }
                    } else {
                        showInfoForm();
                    }
                });
            }
        }
    }

    let submitListenerAdded = false;

    function showInfoForm() {
        if (currProduct === 'Lanyards') {
            $lanyardInfo.classList.remove('d-none');
        } else {
            $lanyardInfo.classList.add('d-none');
        }
        $fieldset.appendChild($infoContainer);
        $infoContainer.classList.remove('d-none');
        $infoContainer.scrollIntoView({ behavior: 'smooth' });

        if (submitListenerAdded) {
            return;
        }
        submitListenerAdded = true;

        $form.addEventListener('submit', async e => {
            e.preventDefault();
            if (!ReportValidity($form)) {
                return false;
            }

            $fieldset.setAttribute('disabled', 'true');
            buttons.Start($submitBtn);

            if (currProduct === 'Lanyards') {
                const $lanyardText = $lanyardInfo.querySelector('#lanyard-text') as HTMLInputElement;
                const $lanyardTextColor = $lanyardInfo.querySelector('#lanyard-text-color') as HTMLInputElement;
                const $lanyardColor = $lanyardInfo.querySelector('#lanyard-color') as HTMLInputElement;

                lanyardExtras = [new models.QuoteRequestCreateExtra({
                    name: 'Custom Lanyard Text',
                    value: $lanyardText.value,
                }), new models.QuoteRequestCreateExtra({
                    name: 'Lanyard Text Color',
                    value: $lanyardTextColor.value,
                }), new models.QuoteRequestCreateExtra({
                    name: 'Lanyard Color',
                    value: $lanyardColor.value,
                })];
            } else {
                lanyardExtras = undefined;
            }

            // Awaited outside the chain — a PUT-failure throw would otherwise skip
            // the catch/finally below and leave the form disabled.
            let uploadIDs: number[];
            try {
                uploadIDs = await u.uploadIDs();
            } catch (err) {
                ShowError(err);
                $fieldset.removeAttribute('disabled');
                buttons.Stop($submitBtn);
                return;
            }

            await quoterequests.NewQuoteRequest({
                form: 'free quote',
                name: `${$firstName.value} ${$lastName.value}`,
                company: '',
                phone: await p.format(),
                email: $email.value,
                lineItem: currProduct,
                type: currProductType,
                size: currSize,
                children: extrasSteps.map(extra => new models.QuoteRequestCreateChild({
                    child: extra.name,
                    option: extra.currentSelection?.value,
                })),
                quantity: parseInt(CleanNumber($quantity.value), 10) || 0,
                budget: parseFloat(CleanNumber($budget.value)) || 0,
                project: $projectName.value,
                dateNeeded: $dateNeeded.value,
                notes: $projectNotes.value,
                uploadIDs,
                extras: lanyardExtras,
            }).then(async resp => {
                const sess = await Session().catch(() => undefined);
                const dataLayer = window.dataLayer || [];
                dataLayer.push({
                    'event': 'express_hugo_freequote_submission',
                    'userData': {
                        'email': sess?.user.emailAddress.email || $email.value,
                        'phone': sess?.user.phoneNumber.phone || await p.format(),
                        'firstName': sess?.user.firstName || $firstName.value,
                        'lastName': sess?.user.lastName || $lastName.value,
                    },
                    'transactionId': resp.id,
                    'total': resp.quotes?.[0].cachedTotal,
                });

                window.location.href = `${Relref('/free-quote-success')}?id=${resp?.quotes?.[0].key || ''}`;
            }).catch(err => {
                ShowError(err);
                $fieldset.removeAttribute('disabled');
                buttons.Stop($submitBtn);
            });
        });
    }

    function createStep(groupTitle: string, classNames: string[], extra?: boolean): HTMLElement {
        const $inputGroup = template.Clone('.input-group-template').firstElementChild as HTMLElement;
        if (extra) {
            $inputGroup.classList.add('d-none');
        }
        for (const className of classNames) {
            $inputGroup.classList.add(imgNameFormat(className));
        }
        const $inputGroupTitle = $inputGroup?.querySelector('h2') as HTMLElement;
        $inputGroupTitle.innerHTML = groupTitle;

        $inputGroup.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
        currStep = $inputGroup;
        return $inputGroup;
    }

    function removeSteps(classNames: string[]) {
        const $steps = Array.from($fieldset.querySelectorAll('.step'));
        for (const $step of $steps) {
            const classList = Array.from($step.classList);

            if (!classList.some(className => classNames.includes(className))) {
                $step.remove();
            }
        }
        $infoContainer.classList.add('d-none');
    }

    function createInput($step: HTMLElement, inputName: string, itemName: string, path?: string, description?: string, displayName?: string) {
        const $inputTemplate = template.Clone('.input-template');
        const $wrapper = $inputTemplate.firstElementChild as HTMLElement;
        const $label = $wrapper.querySelector('label') as HTMLLabelElement;

        ($label.querySelector('h4') as HTMLElement).textContent = displayName || itemName;
        ($label.querySelector('input') as HTMLInputElement).name = inputName;
        ($label.querySelector('input') as HTMLInputElement).value = itemName;

        const $desc = $label.querySelector('.input-desc') as HTMLElement;
        if ($desc && description) {
            $desc.textContent = description;
        } else if ($desc) {
            $desc.remove();
        }

        if (path) {
            const $tmpl = document.querySelector(`.quote-image-template[data-file^="/img/free-quote/${path}"]`);
            if ($tmpl) {
                const $imgTemplate = template.Clone($tmpl as HTMLTemplateElement);
                $label.insertBefore($imgTemplate, $label.firstElementChild);
            }
        }

        $step?.querySelector('.input-container')?.appendChild($inputTemplate);
        if ($step) {
            $fieldset.appendChild($step);
        }

        return $label;
    }

    function checkedAndRequired($inputEl: HTMLInputElement) {
        $inputEl.checked = true;
        $inputEl.required = true;
    }

    function previouslySelected($tar: HTMLElement): boolean {
        if ($tar.closest('.input-container')?.classList.contains('previously-selected')) {
            return true;
        } else {
            $tar.closest('.input-container')?.classList.add('previously-selected');
            return false;
        }
    }

    function addRemoveBorders($tar: HTMLElement, $allInputs: HTMLElement[]) {
        for (const $ of $allInputs) {
            $.classList.remove('border', 'border-primary', 'rounded');
        }
        $tar.classList.add('border', 'border-primary', 'rounded');
    }

    function imgNameFormat(apiStr: string): string {
        return apiStr.replace(/\s+/g, '-').replace(/%/g, '-percent').toLowerCase();
    }
}
